// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import logo from "../assets/images/azps.png";

export default function Sidebar({ 
  rooms, 
  selectedRoomId, 
  onSelectRoom, 
  isCollapsed, 
  onToggleCollapse, 
  searchTerm, 
  onSearchChange,
  onNavigate,
  isOpen,
  onClose,
  isMobile
}) {
  
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');

  const filteredRooms = rooms.filter(room => 
    room.id.toString().includes(searchTerm) ||
    room.roomType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', color: 'blue' },
    { id: 'allocate', label: 'Room Allocation', icon: '🏨', color: 'green' },
    { id: 'tasks', label: 'Task Management', icon: '📋', color: 'yellow' },
    { id: 'staff', label: 'Staff Management', icon: '👥', color: 'purple' },
    { id: 'audit', label: 'Audit Report', icon: '📈', color: 'indigo' },
    { id: 'damage', label: 'Damage Report', icon: '⚠️', color: 'red' },
    { id: 'review', label: 'Issue Review', icon: '🔍', color: 'orange' },
  ];

  const handleMenuItemClick = (itemId) => {
    setActiveMenuItem(itemId);
    onNavigate(itemId);
  };

  // Get sidebar width classes
  const getSidebarWidth = () => {
    if (isCollapsed && !isMobile) return 'w-20';
    return isMobile ? 'w-72' : 'w-80';
  };

  // Get transform classes for mobile
  const getTransformClasses = () => {
    if (!isMobile) return 'translate-x-0';
    return isOpen ? 'translate-x-0' : '-translate-x-full';
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-40
        bg-white text-gray-800 flex flex-col shadow-2xl border-r border-gray-200
        transition-all duration-300 ease-in-out
        ${getSidebarWidth()}
        ${getTransformClasses()}
      `}>
      
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300" />
                <div className="relative">
                  <img 
                    src={logo} 
                    alt="Aizops Logo" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
              </div>
              
              {(!isCollapsed || isMobile) && (
                <div className="flex flex-col">
                  <h1 className="font-bold text-lg text-gray-800">Aizops</h1>
                  <p className="text-xs font-semibold text-blue-600">Smart Housekeeping</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Desktop collapse button */}
              {!isMobile && (
                <button
                  onClick={onToggleCollapse}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-700"
                  aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7m8 14l-7-7 7-7"} />
                  </svg>
                </button>
              )}
              
              {/* Mobile close button */}
              {isMobile && isOpen && (
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-700"
                  aria-label="Close sidebar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {(!isCollapsed || isMobile) && (
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="relative">
              <input
                type="text"
                placeholder="Search rooms..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
              <span className="absolute left-3 top-3 text-gray-400">🔍</span>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-4">
            {(!isCollapsed || isMobile) && (
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Main Menu</p>
            )}
            <div className="space-y-1.5">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-xl 
                    transition-all duration-200 group relative
                    ${activeMenuItem === item.id 
                      ? `bg-${item.color}-50 text-${item.color}-700 shadow-sm` 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  <span className="text-xl">{item.icon}</span>
                  {(!isCollapsed || isMobile) && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                  {isCollapsed && !isMobile && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Rooms List */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            {(!isCollapsed || isMobile) && (
              <div className="flex justify-between items-center mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Rooms List</p>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  {filteredRooms.length} rooms
                </span>
              </div>
            )}
            
            <div className="space-y-2">
              {filteredRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => onSelectRoom(room.id)}
                  className={`
                    w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 group relative
                    ${selectedRoomId === room.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm'
                      : 'text-gray-600 hover:bg-white hover:shadow-sm'
                    }
                  `}
                >
                  <div className="relative">
                    <span className="text-lg">🏠</span>
                    {!room.isProcessed && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>
                    )}
                  </div>
                  
                  {(!isCollapsed || isMobile) && (
                    <div className="flex-1 text-left">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Room {room.id}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          room.isProcessed 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {room.isProcessed ? 'Cleaned' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-0.5">
                        <span className="text-xs text-gray-500">{room.roomType}</span>
                        <span className="text-xs text-gray-400">Floor {room.floor}</span>
                      </div>
                    </div>
                  )}
                  
                  {isCollapsed && !isMobile && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50">
                      Room {room.id} - {room.roomType} - {room.isProcessed ? 'Cleaned' : 'Pending'}
                    </div>
                  )}
                </button>
              ))}
              
              {filteredRooms.length === 0 && (!isCollapsed || isMobile) && (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-400">No rooms found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Staff Info Footer */}
        <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
              <span className="text-white text-lg">👤</span>
            </div>
            {(!isCollapsed || isMobile) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 truncate">Staff Portal</p>
                <p className="text-xs text-blue-600 truncate">Housekeeping Team</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles are defined in src/index.css */}
    </>
  );
}